/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { FormattedMessage } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { INCOMING, OUTGOING } from '../../../../common/constants/dependency.tsx';
import type {
	Appearance,
	OverlayItem,
	ImplicitDependencyItem,
	OnUnlinkCallback,
} from '../../../../common/types/dependency.tsx';
import ExpandableCard from '../../../../common/ui/dependency/expandable-card/index.tsx';
import messages from './messages.tsx';

const implicitTitleStylesOld = css({
	margin: `${token('space.250', '20px')} 0 ${token('space.150', '12px')} 0`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:first-child': {
		marginTop: 0,
	},
});

const implicitTitleStyles = css({
	margin: `0 0 ${token('space.150', '12px')} 0`,
});

type Props = {
	parentItem: OverlayItem;
	implicitDependencyItems: ImplicitDependencyItem[];
	outwardDependencyDescription: string;
	inwardDependencyDescription: string;
	onClose: () => void;
	onUnlink?: OnUnlinkCallback; // optional handler, controls visibility of the unlink button
};

const ImplicitDependency = ({
	parentItem: { itemKey, title },
	implicitDependencyItems,
	inwardDependencyDescription,
	outwardDependencyDescription,
	...otherProps
}: Props) => {
	const incoming = implicitDependencyItems.filter(
		({ dependencies }: ImplicitDependencyItem) => dependencies.length > 0,
	);
	const outgoing = implicitDependencyItems.filter(
		({ dependees }: ImplicitDependencyItem) => dependees.length > 0,
	);

	if (incoming.length === 0 && outgoing.length === 0) {
		return null;
	}

	const renderDependencyItems = (
		items: ImplicitDependencyItem[],
		appearance: Appearance,
		messageDescriptorSingular: MessageDescriptor,
		messageDescriptorPlural: MessageDescriptor,
		description: string,
	) => {
		if (!items.length) {
			return null;
		}

		const messageDescriptor =
			items.length === 1 ? messageDescriptorSingular : messageDescriptorPlural;
		const dependencyItemsKey = appearance === INCOMING ? 'dependencies' : 'dependees';

		return (
			<>
				{fg('jsw_roadmaps_timeline-post-project-a11y-fix') ? (
					<div css={implicitTitleStyles}>
						<Heading as="h2" size="xsmall">
							<Text weight="regular">
								<FormattedMessage
									{...messageDescriptor}
									values={{
										count: items.length,
										itemKey,
										title,
										// @ts-expect-error - Type '(chunks: string) => JSX.Element' is not assignable to type 'ReactNode'.
										b: (chunks: string) => <b>{chunks}</b>,
									}}
								/>
							</Text>
						</Heading>
					</div>
				) : (
					<div css={implicitTitleStylesOld}>
						<FormattedMessage
							{...messageDescriptor}
							values={{
								count: items.length,
								itemKey,
								title,
								// @ts-expect-error - Type '(chunks: string) => JSX.Element' is not assignable to type 'ReactNode'.
								b: (chunks: string) => <b>{chunks}</b>,
							}}
						/>
					</div>
				)}

				{items.map(({ item, ...rest }: ImplicitDependencyItem) => (
					<ExpandableCard
						key={item.id}
						appearance={appearance}
						{...otherProps}
						item={item}
						dependencyItems={rest[dependencyItemsKey]}
						description={description}
					/>
				))}
			</>
		);
	};

	return (
		<>
			{renderDependencyItems(
				incoming,
				INCOMING,
				expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.singleIncomingDependencyIssueTermRefresh
					: messages.singleIncomingDependency,
				expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.incomingDependenciesIssueTermRefresh
					: messages.incomingDependencies,
				inwardDependencyDescription,
			)}
			{renderDependencyItems(
				outgoing,
				OUTGOING,
				expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.singleOutgoingDependencyIssueTermRefresh
					: messages.singleOutgoingDependency,
				expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.outgoingDependenciesIssueTermRefresh
					: messages.outgoingDependencies,
				outwardDependencyDescription,
			)}
		</>
	);
};

export default ImplicitDependency;
